// The colour theme: light, dark or as the system (docs/redesign-stage2-2026-09-28.md,
// part 3). The choice is kept in localStorage under "theme"; index.html
// applies it before the first paint by the same rules, and this module keeps
// the page in step afterwards - a choice made in a menu, or the system
// turning dark or light while "as the system" is on.
export type ThemeChoice = 'light' | 'dark' | 'system'
export type Theme = 'light' | 'dark'

const KEY = 'theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

const darkQuery = () =>
  typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DARK_QUERY) : null

const readChoice = (): ThemeChoice => {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

const resolve = (value: ThemeChoice): Theme =>
  value === 'system' ? (darkQuery()?.matches ? 'dark' : 'light') : value

// "only light": a browser that darkens light sites by itself (Chrome's auto
// dark mode, Samsung Internet) leaves a light page alone
const apply = (value: Theme) => {
  const root = document.documentElement
  root.classList.toggle('dark', value === 'dark')
  root.style.colorScheme = value === 'dark' ? 'dark' : 'only light'
}

let choice: ThemeChoice = readChoice()
let theme: Theme = resolve(choice)
const listeners = new Set<() => void>()

const update = () => {
  theme = resolve(choice)
  apply(theme)
  listeners.forEach((listener) => listener())
}

export const setThemeChoice = (next: ThemeChoice) => {
  choice = next
  try {
    if (next === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, next)
  } catch {
    // storage blocked: the choice holds until the page is closed
  }
  update()
}

export const getThemeChoice = () => choice
export const getTheme = () => theme

export const subscribeTheme = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Once at start: the theme index.html set, and the system's changes while the
// choice is "as the system"
export const startTheme = () => {
  update()
  darkQuery()?.addEventListener('change', () => {
    if (choice === 'system') update()
  })
}
